/**
 * Super-admin review surface for support requests + feedback submitted from the Help page.
 * Lists items from the backend intake endpoint and lets a super-admin update each item's status.
 * Access is enforced server-side (disclosureAdmin only); this page surfaces a friendly message if
 * the caller isn't authorized.
 */
import React, { useEffect, useState, useCallback } from "react";
import {
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Select,
  MenuItem,
  Chip,
  Button,
  CircularProgress,
  Box,
  Snackbar,
  Alert,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  listSupportRequests,
  updateSupportStatus,
} from "../../utils/supportService";

const STATUS_OPTIONS = ["open", "inProgress", "closed"];
const STATUS_COLOR = { open: "warning", inProgress: "info", closed: "success" };

const SupportReview = () => {
  const theme = useTheme();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [snack, setSnack] = useState({ open: false, severity: "success", msg: "" });

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await listSupportRequests();
      setItems(Array.isArray(res?.data) ? res.data : []);
    } catch (e) {
      if (e?.response?.status === 401) {
        setError("You don't have access to review support requests.");
      } else {
        setError("Couldn't load support requests. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleStatus = async (id, status) => {
    // optimistic update
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, status } : it)));
    try {
      await updateSupportStatus(id, status);
      setSnack({ open: true, severity: "success", msg: "Status updated." });
    } catch (e) {
      setSnack({ open: true, severity: "error", msg: "Couldn't update status." });
      load();
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return isNaN(d) ? iso : d.toLocaleString();
  };

  const cellHead = { fontWeight: 700, color: theme.palette.custom.black };

  return (
    // Box, not Container: the app layout removes all padding from Containers.
    <Box
      sx={{
        backgroundColor: theme.palette.common.white,
        minHeight: "100vh",
        overflowY: "auto",
        padding: "24px 32px",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.custom.black }}>
          Support & Feedback
        </Typography>
        <Button variant="outlined" onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Box>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", padding: "48px" }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && (
        <Alert severity="warning" sx={{ my: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && items.length === 0 && (
        <Typography sx={{ color: theme.palette.custom.gray6, padding: "24px 0" }}>
          No support requests or feedback yet.
        </Typography>
      )}

      {!loading && !error && items.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={cellHead}>Type</TableCell>
              <TableCell sx={cellHead}>Detail</TableCell>
              <TableCell sx={cellHead}>Message</TableCell>
              <TableCell sx={cellHead}>From</TableCell>
              <TableCell sx={cellHead}>Submitted</TableCell>
              <TableCell sx={cellHead}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((it) => (
              <TableRow key={it.id} hover>
                <TableCell>
                  <Chip
                    size="small"
                    label={it.kind === "feedback" ? "Feedback" : "Support"}
                    color={it.kind === "feedback" ? "secondary" : "primary"}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  {it.kind === "feedback" ? (
                    <>
                      {it.feedbackType || "Feedback"}
                      {it.rating ? ` · ${it.rating}/5` : ""}
                    </>
                  ) : (
                    <>
                      <div style={{ fontWeight: 600 }}>{it.subject || "(no subject)"}</div>
                      <div style={{ color: theme.palette.custom.gray6 }}>{it.category}</div>
                    </>
                  )}
                </TableCell>
                <TableCell sx={{ maxWidth: 360, whiteSpace: "pre-wrap" }}>{it.message}</TableCell>
                <TableCell>
                  <div>{it.reporterName || "—"}</div>
                  <div style={{ color: theme.palette.custom.gray6, fontSize: "0.8rem" }}>
                    {it.reporterEmail}
                  </div>
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>{fmtDate(it.createdAt)}</TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Chip
                      size="small"
                      label={it.status || "open"}
                      color={STATUS_COLOR[it.status] || "default"}
                    />
                    <Select
                      size="small"
                      value={it.status || "open"}
                      onChange={(e) => handleStatus(it.id, e.target.value)}
                    >
                      {STATUS_OPTIONS.map((s) => (
                        <MenuItem key={s} value={s}>
                          {s}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ width: "100%" }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SupportReview;
